<template>
  <BaseTemplate app-name="Gestão de Permissões de Grupo" page-title="">
    <template #content>
      <div class="w-full max-w-6xl mx-auto p-6">
        <!-- Painel de Resumo -->
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                      <div class="bg-white dark:bg-gray-800 rounded-lg shadow flex items-center gap-3 p-4">
            <Users class="w-7 h-7 text-blue-500" />
            <div>
              <div class="text-lg font-bold text-black dark:text-white">{{ groups.length }}</div>
              <div class="text-xs text-gray-500 dark:text-gray-400">Grupos</div>
            </div>
          </div>
          <div class="bg-white dark:bg-gray-800 rounded-lg shadow flex items-center gap-3 p-4">
            <Shield class="w-7 h-7 text-emerald-500" />
            <div>
              <div class="text-lg font-bold text-black dark:text-white">{{ permissions.length }}</div>
              <div class="text-xs text-gray-500 dark:text-gray-400">Permissões</div>
            </div>
          </div>
          <div class="bg-white dark:bg-gray-800 rounded-lg shadow flex items-center gap-3 p-4">
            <User class="w-7 h-7 text-pink-500" />
            <div>
              <div class="text-lg font-bold text-black dark:text-white">{{ totalUsers }}</div>
              <div class="text-xs text-gray-500 dark:text-gray-400">Usuários</div>
            </div>
          </div>
        </div>
        <!-- Título e subtítulo -->
        <div class="flex flex-col md:flex-row md:items-center md:justify-between mb-4 gap-2">
          <div>
            <h1 class="flex items-center gap-2 text-2xl font-bold" :style="{ color: 'var(--clinic-font-color)' }">
              <Shield class="w-6 h-6" :style="{ color: 'var(--clinic-icon-color)' }" />
              Gestão de Permissões de Grupo
            </h1>
            <p class="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-2xl">Gerencie os grupos de usuários do sistema, atribua permissões e visualize rapidamente quantos usuários e permissões cada grupo possui. Grupos controlam o acesso a funcionalidades do sistema.</p>
          </div>
          <button
            @click="voltar"
            class="inline-flex items-center gap-2 clinic-btn text-white px-4 py-2 rounded hover:bg-blue-600 transition-colors self-start md:self-auto"
          >
            <ArrowLeft class="w-5 h-5" />
            <span>Voltar</span>
          </button>
        </div>
        <!-- Tabela de grupos (desktop) -->
        <div class="hidden md:block bg-white dark:bg-gray-800 rounded-lg shadow overflow-x-auto mb-8">
          <table class="w-full min-w-[600px]">
            <thead class="bg-gray-50 dark:bg-gray-700 text-sm uppercase tracking-wide rounded-t-lg sticky top-0 z-10">
              <tr>
                <th class="px-4 py-3 text-left" :style="{ color: 'var(--clinic-font-color)' }">Grupo</th>
                <th class="px-4 py-3 text-center" :style="{ color: 'var(--clinic-font-color)' }">Usuários</th>
                <th class="px-4 py-3 text-center" :style="{ color: 'var(--clinic-font-color)' }">Permissões Atribuídas</th>
                <th class="px-4 py-3 text-center" :style="{ color: 'var(--clinic-font-color)' }">Ações</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="group in groups" :key="group.id" class="border-b dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                <td class="px-4 py-3 flex items-center gap-2 clinic-text">
                  <span v-if="group.name === 'superadmin'">
                    <Star class="w-5 h-5 text-yellow-400" />
                  </span>
                  <span v-else-if="group.name === 'medico'">
                    <GraduationCap class="w-5 h-5 text-blue-400" />
                  </span>
                  <span v-else-if="group.name === 'monitor'">
                    <Monitor class="w-5 h-5 text-green-400" />
                  </span>
                  <span v-else>
                    <Clock class="w-5 h-5 text-gray-400" />
                  </span>
                  {{ group.name }}
                </td>
                <td class="px-4 py-3 text-center text-black dark:text-white">{{ group.user_count || 0 }}</td>
                <td class="px-4 py-3 text-center text-black dark:text-white">{{ group.permission_count || 0 }}</td>
                <td class="px-4 py-3 text-center">
                  <button @click="openModal(group)" class="flex items-center gap-1" :style="{ color: 'var(--clinic-icon-color)' }">
                    <Edit class="w-4 h-4" />
                    Editar
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <!-- Cards de grupos (mobile) -->
        <div class="md:hidden space-y-4 mb-8">
          <div v-for="group in groups" :key="group.id" class="bg-white dark:bg-gray-800 shadow-lg rounded-xl p-4 border border-gray-100 dark:border-gray-700 transition-all hover:shadow-xl">
            <div class="flex items-center justify-between mb-3">
              <div class="flex items-center gap-3">
                <span v-if="group.name === 'superadmin'" class="p-2 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg">
                  <Star class="w-5 h-5 text-yellow-500" />
                </span>
                <span v-else-if="group.name === 'medico'" class="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                  <GraduationCap class="w-5 h-5 text-blue-500" />
                </span>
                <span v-else-if="group.name === 'monitor'" class="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                  <Monitor class="w-5 h-5 text-green-500" />
                </span>
                <span v-else class="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                  <Clock class="w-5 h-5 text-gray-500" />
                </span>
                <div>
                  <h3 class="text-lg font-bold clinic-text">{{ group.name }}</h3>
                  <p class="text-xs text-gray-500 dark:text-gray-400 capitalize">{{ group.name }}</p>
                </div>
              </div>
              <button @click="openModal(group)" class="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors" :style="{ color: 'var(--clinic-icon-color)' }">
                <Edit class="w-5 h-5" />
              </button>
            </div>
            <div class="grid grid-cols-2 gap-4 mb-4">
              <div class="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 text-center">
                <div class="text-lg font-bold text-black dark:text-white">{{ group.user_count || 0 }}</div>
                <div class="text-xs text-gray-500 dark:text-gray-400">Usuários</div>
              </div>
              <div class="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 text-center">
                <div class="text-lg font-bold text-black dark:text-white">{{ group.permission_count || 0 }}</div>
                <div class="text-xs text-gray-500 dark:text-gray-400">Permissões</div>
              </div>
            </div>
            <button @click="openModal(group)" class="w-full clinic-btn text-white py-2 px-4 rounded-lg text-sm font-medium transition-colors">
              Gerenciar Permissões
            </button>
          </div>
        </div>
        <!-- Modal de edição de permissões do grupo -->
        <div v-if="isModalOpen" class="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-0 md:p-4" @click.self="closeModal">
          <div class="bg-white dark:bg-gray-800 rounded-lg shadow-lg w-full max-w-3xl animate-fade-in modal-mobile-full">
            <!-- Header do Modal -->
            <div class="p-4 border-b border-gray-200 dark:border-gray-700">
              <div class="flex items-center justify-between">
                <h2 class="flex items-center gap-2 text-lg font-semibold clinic-text text-black dark:text-white">
                  <Shield class="w-5 h-5" :style="{ color: 'var(--clinic-icon-color)' }" />
                  {{ currentGroup.name }}
                </h2>
                <button @click="closeModal" class="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                  <X class="w-5 h-5" />
                </button>
              </div>
            </div>

            <!-- Conteúdo do Modal -->
            <div class="p-4 overflow-y-auto max-h-[calc(100vh-120px)] modal-mobile-content">
              <!-- Versão Desktop -->
              <div class="hidden md:grid md:grid-cols-[1fr_auto_1fr] gap-4 items-start">
                <!-- Permissões Disponíveis -->
                <div class="flex flex-col gap-2">
                  <label for="perm-search-left" class="text-sm font-medium text-gray-700 dark:text-gray-300">Permissões Disponíveis</label>
                  <div class="flex gap-2">
                    <div class="relative flex-1">
                      <input id="perm-search-left" type="text" v-model="searchPermAvailable" placeholder="Buscar permissão..."
                        class="h-10 w-full px-3 pl-10 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                      <Search class="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    </div>
                    <select v-model="filterAppAvailable" class="h-10 px-3 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                      <option value="">Todos os apps</option>
                      <option v-for="app in availableApps" :key="app" :value="app">{{ translateAppLabel(app) }}</option>
                    </select>
                  </div>
                  <div class="border rounded-md bg-white dark:bg-gray-900 dark:border-gray-700 h-72 overflow-y-auto">
                    <div v-if="Object.keys(availablePermissionsGrouped).length > 0">
                      <div v-for="(perms, app) in availablePermissionsGrouped" :key="app" class="border-b border-gray-100 dark:border-gray-700 last:border-b-0">
                        <div class="px-3 py-2 bg-gray-50 dark:bg-gray-800 text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                          {{ translateAppLabel(app) }}
                        </div>
                        <ul>
                          <li v-for="perm in perms" :key="perm.id"
                            @click="togglePermissionSelection('available', perm.id)"
                            class="px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-700 last:border-b-0"
                            :class="{ 'bg-blue-100 dark:bg-blue-900/50': selectedAvailablePermissions.includes(perm.id) }">
                            <div class="font-medium text-black dark:text-white">{{ perm.name }}</div>
                            <div class="text-xs text-gray-500 dark:text-gray-400">
                              <span class="bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-1.5 py-0.5 rounded text-xs font-medium mr-1">{{ perm.app_label }}</span>
                              {{ perm.codename }}
                            </div>
                          </li>
                        </ul>
                      </div>
                    </div>
                    <div v-else class="text-center text-sm text-gray-500 p-4">Nenhuma permissão disponível.</div>
                  </div>
                </div>
                <!-- Botões de Controle -->
                <div class="flex flex-col gap-2 self-center pt-16">
                  <button @click="moveSelectedToRight" :disabled="selectedAvailablePermissions.length === 0" class="p-2 border rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed" aria-label="Atribuir selecionadas">&gt;</button>
                  <button @click="moveAllToRight" :disabled="availablePermissions.length === 0" class="p-2 border rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed" aria-label="Atribuir todas">&gt;&gt;</button>
                  <button @click="moveSelectedToLeft" :disabled="selectedAssignedPermissions.length === 0" class="p-2 border rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed" aria-label="Remover selecionadas">&lt;</button>
                  <button @click="moveAllToLeft" :disabled="assignedPermissions.length === 0" class="p-2 border rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed" aria-label="Remover todas">&lt;&lt;</button>
                </div>
                <!-- Permissões Atribuídas -->
                <div class="flex flex-col gap-2">
                  <label for="perm-search-right" class="text-sm font-medium text-gray-700 dark:text-gray-300">Permissões Atribuídas</label>
                  <div class="flex gap-2">
                    <div class="relative flex-1">
                      <input id="perm-search-right" type="text" v-model="searchPermAssigned" placeholder="Buscar permissão..."
                        class="h-10 w-full px-3 pl-10 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                      <Search class="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    </div>
                    <select v-model="filterAppAssigned" class="h-10 px-3 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                      <option value="">Todos os apps</option>
                      <option v-for="app in availableApps" :key="app" :value="app">{{ translateAppLabel(app) }}</option>
                    </select>
                  </div>
                  <div class="border rounded-md bg-white dark:bg-gray-900 dark:border-gray-700 h-72 overflow-y-auto">
                    <div v-if="Object.keys(assignedPermissionsGrouped).length > 0">
                      <div v-for="(perms, app) in assignedPermissionsGrouped" :key="app" class="border-b border-gray-100 dark:border-gray-700 last:border-b-0">
                        <div class="px-3 py-2 bg-gray-50 dark:bg-gray-800 text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                          {{ translateAppLabel(app) }}
                        </div>
                        <ul>
                          <li v-for="perm in perms" :key="perm.id"
                            @click="togglePermissionSelection('assigned', perm.id)"
                            class="px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-700 last:border-b-0"
                            :class="{ 'bg-blue-100 dark:bg-blue-900/50': selectedAssignedPermissions.includes(perm.id) }">
                            <div class="font-medium text-black dark:text-white">{{ perm.name }}</div>
                            <div class="text-xs text-gray-500 dark:text-gray-400">
                              <span class="bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-1.5 py-0.5 rounded text-xs font-medium mr-1">{{ perm.app_label }}</span>
                              {{ perm.codename }}
                            </div>
                          </li>
                        </ul>
                      </div>
                    </div>
                    <div v-else class="text-center text-sm text-gray-500 p-4">Nenhuma permissão atribuída.</div>
                  </div>
                </div>
              </div>

              <!-- Versão Mobile -->
              <div class="md:hidden space-y-6">
                <!-- Seletor de Abas -->
                <div class="flex border-b border-gray-200 dark:border-gray-700">
                  <button 
                    @click="mobileTab = 'available'" 
                    class="flex-1 py-2 px-4 text-sm font-medium transition-colors"
                    :class="mobileTab === 'available' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'"
                  >
                    Disponíveis ({{ availablePermissions.length }})
                  </button>
                  <button 
                    @click="mobileTab = 'assigned'" 
                    class="flex-1 py-2 px-4 text-sm font-medium transition-colors"
                    :class="mobileTab === 'assigned' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'"
                  >
                    Atribuídas ({{ assignedPermissions.length }})
                  </button>
                </div>

                <!-- Filtros -->
                <div class="space-y-3">
                  <div class="relative">
                    <input 
                      :id="mobileTab === 'available' ? 'mobile-search-available' : 'mobile-search-assigned'"
                      type="text" 
                      :value="mobileTab === 'available' ? searchPermAvailable : searchPermAssigned"
                      @input="mobileTab === 'available' ? searchPermAvailable = $event.target.value : searchPermAssigned = $event.target.value"
                      :placeholder="mobileTab === 'available' ? 'Buscar permissões disponíveis...' : 'Buscar permissões atribuídas...'"
                      class="w-full px-3 pl-10 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <Search class="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  </div>
                  <select 
                    :value="mobileTab === 'available' ? filterAppAvailable : filterAppAssigned"
                    @change="mobileTab === 'available' ? filterAppAvailable = $event.target.value : filterAppAssigned = $event.target.value"
                    class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Todos os apps</option>
                    <option v-for="app in availableApps" :key="app" :value="app">{{ translateAppLabel(app) }}</option>
                  </select>
                </div>

                <!-- Lista de Permissões -->
                <div class="max-h-96 overflow-y-auto">
                  <div v-if="mobileTab === 'available'">
                    <div v-if="Object.keys(availablePermissionsGrouped).length > 0">
                      <div v-for="(perms, app) in availablePermissionsGrouped" :key="app" class="mb-4">
                        <div class="px-3 py-2 bg-gray-50 dark:bg-gray-800 text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide rounded-t-lg">
                          {{ translateAppLabel(app) }}
                        </div>
                        <div class="border border-gray-200 dark:border-gray-700 rounded-b-lg overflow-hidden">
                          <div 
                            v-for="perm in perms" 
                            :key="perm.id"
                            @click="togglePermissionSelection('available', perm.id)"
                            class="px-3 py-3 cursor-pointer border-b border-gray-100 dark:border-gray-700 last:border-b-0 transition-colors"
                            :class="{ 'bg-blue-50 dark:bg-blue-900/30': selectedAvailablePermissions.includes(perm.id) }"
                          >
                            <div class="flex items-center justify-between">
                              <div class="flex-1">
                                <div class="font-medium text-black dark:text-white text-sm">{{ translatePermissionName(perm) }}</div>
                                <div class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                  <span class="bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-1.5 py-0.5 rounded text-xs font-medium mr-1">{{ translateAppLabel(perm.app_label) }}</span>
                                  {{ perm.codename }}
                                </div>
                              </div>
                              <div v-if="selectedAvailablePermissions.includes(perm.id)" class="ml-2">
                                <Check class="w-4 h-4 text-blue-600" />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div v-else class="text-center text-sm text-gray-500 p-8">
                      Nenhuma permissão disponível.
                    </div>
                  </div>

                  <div v-else>
                    <div v-if="Object.keys(assignedPermissionsGrouped).length > 0">
                      <div v-for="(perms, app) in assignedPermissionsGrouped" :key="app" class="mb-4">
                        <div class="px-3 py-2 bg-gray-50 dark:bg-gray-800 text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide rounded-t-lg">
                          {{ translateAppLabel(app) }}
                        </div>
                        <div class="border border-gray-200 dark:border-gray-700 rounded-b-lg overflow-hidden">
                          <div 
                            v-for="perm in perms" 
                            :key="perm.id"
                            @click="togglePermissionSelection('assigned', perm.id)"
                            class="px-3 py-3 cursor-pointer border-b border-gray-100 dark:border-gray-700 last:border-b-0 transition-colors"
                            :class="{ 'bg-blue-50 dark:bg-blue-900/30': selectedAssignedPermissions.includes(perm.id) }"
                          >
                            <div class="flex items-center justify-between">
                              <div class="flex-1">
                                <div class="font-medium text-black dark:text-white text-sm">{{ translatePermissionName(perm) }}</div>
                                <div class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                  <span class="bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-1.5 py-0.5 rounded text-xs font-medium mr-1">{{ translateAppLabel(perm.app_label) }}</span>
                                  {{ perm.codename }}
                                </div>
                              </div>
                              <div v-if="selectedAssignedPermissions.includes(perm.id)" class="ml-2">
                                <Check class="w-4 h-4 text-blue-600" />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div v-else class="text-center text-sm text-gray-500 p-8">
                      Nenhuma permissão atribuída.
                    </div>
                  </div>
                </div>

                <!-- Botões de Ação Mobile -->
                <div class="flex gap-2 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <button 
                    v-if="mobileTab === 'available'"
                    @click="moveSelectedToRight" 
                    :disabled="selectedAvailablePermissions.length === 0"
                    class="flex-1 clinic-btn text-white py-2 px-4 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Adicionar Selecionadas ({{ selectedAvailablePermissions.length }})
                  </button>
                  <button 
                    v-if="mobileTab === 'assigned'"
                    @click="moveSelectedToLeft" 
                    :disabled="selectedAssignedPermissions.length === 0"
                    class="flex-1 bg-red-500 hover:bg-red-600 text-white py-2 px-4 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Remover Selecionadas ({{ selectedAssignedPermissions.length }})
                  </button>
                </div>
              </div>
            </div>

            <!-- Footer do Modal -->
            <div class="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700">
              <div class="flex flex-col sm:flex-row gap-2 justify-end">
                <button @click="closeModal" class="flex items-center justify-center gap-1 bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg text-sm">
                  <X class="w-4 h-4" />
                  Cancelar
                </button>
                <button @click="savePermissions" class="flex items-center justify-center gap-1 clinic-btn text-white px-4 py-2 rounded-lg text-sm">
                  <Check class="w-4 h-4" />
                  Salvar Alterações
                </button>
              </div>
              <div v-if="successMsg || errorMsg" class="mt-3">
                <p v-if="successMsg" class="text-green-600 text-sm">{{ successMsg }}</p>
                <p v-if="errorMsg" class="text-red-600 text-sm">{{ errorMsg }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <p class="text-center text-sm mb-11 mt-10 clinic-text">
        © Desenvolvido por Tecnologia da Informação - CCDTI © Todos os direitos reservados
      </p>
    </template>
  </BaseTemplate>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import BaseTemplate from '../templates/BaseTemplate.vue';
import apiClient from '@/services/authService';
import { useRouter } from 'vue-router';
import { useClinicStore } from '@/stores/settings/clinicStore';
import { 
  Users, 
  Shield, 
  User, 
  ArrowLeft, 
  Star, 
  GraduationCap, 
  Monitor, 
  Clock, 
  Edit, 
  Search, 
  X, 
  Check 
} from 'lucide-vue-next';

const clinicStore = useClinicStore();
clinicStore.initialize();
const router = useRouter();
const voltar = () => router.go(-1);

const groups = ref([]);
const permissions = ref([]);
const selectedPermissions = ref([]);
const isModalOpen = ref(false);
const currentGroup = ref({});
const successMsg = ref('');
const errorMsg = ref('');
const searchPermAvailable = ref("");
const searchPermAssigned = ref("");
const filterAppAvailable = ref("");
const filterAppAssigned = ref("");
const selectedAvailablePermissions = ref([]);
const selectedAssignedPermissions = ref([]);
const totalUsers = ref(0);
const mobileTab = ref('available');

onMounted(() => {
  fetchGroups();
  fetchPermissions();
});

async function fetchGroups() {
  try {
    const res = await apiClient.get('/api/v1/auth/groups/');
    // Buscar usuários e permissões de cada grupo
    const usersRes = await apiClient.get('/api/v1/auth/users/');
    totalUsers.value = usersRes.data.length;
    // Montar contagem de usuários e permissões por grupo
    const groupUserCount = {};
    usersRes.data.forEach(u => {
      (u.groups || []).forEach(g => {
        groupUserCount[g] = (groupUserCount[g] || 0) + 1;
      });
    });
    const groupPermCount = {};
    for (const group of res.data) {
      // Buscar permissões do grupo
      const perms = await apiClient.get(`/api/v1/auth/groups/${group.id}/permissions/`);
      groupPermCount[group.name] = perms.data.length;
    }
    groups.value = res.data.map(g => ({
      ...g,
      user_count: groupUserCount[g.name] || 0,
      permission_count: groupPermCount[g.name] || 0
    }));
  } catch (e) {
    errorMsg.value = 'Erro ao carregar grupos';
  }
}

async function fetchPermissions() {
  try {
    const res = await apiClient.get('/api/v1/auth/permissions/');
    permissions.value = res.data;
  } catch (e) {
    errorMsg.value = 'Erro ao carregar permissões';
  }
}

async function openModal(group) {
  currentGroup.value = group;
  isModalOpen.value = true;
  try {
    const res = await apiClient.get(`/api/v1/auth/groups/${group.id}/permissions/`);
    selectedPermissions.value = res.data.map(p => p.id);
    successMsg.value = '';
    errorMsg.value = '';
  } catch (e) {
    errorMsg.value = 'Erro ao carregar permissões do grupo';
  }
}

function closeModal() {
  isModalOpen.value = false;
  currentGroup.value = {};
  selectedPermissions.value = [];
  successMsg.value = '';
  errorMsg.value = '';
  searchPermAvailable.value = '';
  searchPermAssigned.value = '';
  filterAppAvailable.value = '';
  filterAppAssigned.value = '';
  selectedAvailablePermissions.value = [];
  selectedAssignedPermissions.value = [];
  mobileTab.value = 'available';
}

async function savePermissions() {
  try {
    console.log('Salvando permissões:', {
      groupId: currentGroup.value.id,
      permissions: selectedPermissions.value
    });
    
    const response = await apiClient.put(`/api/v1/auth/groups/${currentGroup.value.id}/permissions/`, {
      permissions: selectedPermissions.value
    });
    
    console.log('Resposta do servidor:', response);
    successMsg.value = 'Permissões atualizadas com sucesso!';
    errorMsg.value = '';
    
    // Atualizar a contagem de permissões do grupo
    const groupIndex = groups.value.findIndex(g => g.id === currentGroup.value.id);
    if (groupIndex !== -1) {
      groups.value[groupIndex].permission_count = selectedPermissions.value.length;
    }
    
  } catch (e) {
    console.error('Erro ao salvar permissões:', e);
    console.error('Detalhes do erro:', e.response?.data);
    errorMsg.value = e.response?.data?.detail || 'Erro ao salvar permissões';
    successMsg.value = '';
  }
}

const assignedPermissions = computed(() => {
  // Permissões atribuídas ao grupo (painel da direita)
  return permissions.value.filter(p => selectedPermissions.value.includes(p.id)).filter(p => {
    // Filtro por texto
    if (searchPermAssigned.value) {
      const term = searchPermAssigned.value.toLowerCase();
      const matchesText = p.name.toLowerCase().includes(term) || 
                         p.codename.toLowerCase().includes(term) || 
                         (p.app_label && p.app_label.toLowerCase().includes(term));
      if (!matchesText) return false;
    }
    
    // Filtro por app
    if (filterAppAssigned.value && p.app_label !== filterAppAssigned.value) {
      return false;
    }
    
    return true;
  });
});
const availablePermissions = computed(() => {
  // Permissões não atribuídas ao grupo (painel da esquerda)
  return permissions.value.filter(p => !selectedPermissions.value.includes(p.id)).filter(p => {
    // Filtro por texto
    if (searchPermAvailable.value) {
      const term = searchPermAvailable.value.toLowerCase();
      const matchesText = p.name.toLowerCase().includes(term) || 
                         p.codename.toLowerCase().includes(term) || 
                         (p.app_label && p.app_label.toLowerCase().includes(term));
      if (!matchesText) return false;
    }
    
    // Filtro por app
    if (filterAppAvailable.value && p.app_label !== filterAppAvailable.value) {
      return false;
    }
    
    return true;
  });
});

// Agrupar permissões por app
const availablePermissionsGrouped = computed(() => {
  const grouped = {};
  availablePermissions.value.forEach(perm => {
    const app = perm.app_label || 'Outros';
    if (!grouped[app]) {
      grouped[app] = [];
    }
    grouped[app].push(perm);
  });
  return grouped;
});

const assignedPermissionsGrouped = computed(() => {
  const grouped = {};
  assignedPermissions.value.forEach(perm => {
    const app = perm.app_label || 'Outros';
    if (!grouped[app]) {
      grouped[app] = [];
    }
    grouped[app].push(perm);
  });
  return grouped;
});



// Lista de apps disponíveis
const availableApps = computed(() => {
  const apps = new Set();
  permissions.value.forEach(perm => {
    if (perm.app_label) {
      apps.add(perm.app_label);
    }
  });
  return Array.from(apps).sort();
});

function togglePermissionSelection(type, permId) {
  const arr = type === 'available' ? selectedAvailablePermissions : selectedAssignedPermissions;
  const idx = arr.value.indexOf(permId);
  if (idx > -1) arr.value.splice(idx, 1);
  else arr.value.push(permId);
}
function moveSelectedToRight() {
  selectedPermissions.value.push(...selectedAvailablePermissions.value);
  selectedAvailablePermissions.value = [];
}
function moveAllToRight() {
  selectedPermissions.value.push(...availablePermissions.value.map(p => p.id));
  selectedAvailablePermissions.value = [];
}
function moveSelectedToLeft() {
  selectedPermissions.value = selectedPermissions.value.filter(id => !selectedAssignedPermissions.value.includes(id));
  selectedAssignedPermissions.value = [];
}
function moveAllToLeft() {
  // Se houver filtro de app, remove apenas as permissões do app filtrado
  if (filterAppAssigned.value) {
    const idsToRemove = assignedPermissions.value
      .filter(p => p.app_label === filterAppAssigned.value)
      .map(p => p.id);
    selectedPermissions.value = selectedPermissions.value.filter(id => !idsToRemove.includes(id));
    selectedAssignedPermissions.value = [];
  } else {
    // Remove todas as permissões normalmente
    selectedPermissions.value = [];
    selectedAssignedPermissions.value = [];
  }
}
// Tradução dos nomes dos apps e permissões
const appLabelTranslations = {
  'Schedules': 'Agendas',
  'patients': 'Pacientes',
  'Doctors': 'Médicos',
  'ClinicSettings': 'Configurações da Clínica',
  'Insurances': 'Convênios',
  'Audit': 'Auditoria',
  'Profile': 'Perfis',
  'Services': 'Serviços',
  'RoomMedical': 'Salas',
  'Notifications': 'Notificações',
  'Reports': 'Relatórios',
  'ExamDelivery': 'Entrega de Exames',
  'ExamRelease': 'Liberação de Exames',
  'EditExam': 'Edição de Exames',
  'MaskLaudos': 'Máscaras de Laudo',
  'Consultancy': 'Consultorias',
  'Pacs': 'PACS',
  'RecallAppointments': 'Reconvocação',
  // Adicione outros conforme necessário
};
const permissionTranslations = {
  'add_appointment': 'Adicionar Agendamento',
  'change_appointment': 'Editar Agendamento',
  'delete_appointment': 'Excluir Agendamento',
  'view_appointment': 'Visualizar Agendamento',
  'import_sisreg_schedule': 'Importar Agenda SISREG',
  'add_special_slot': 'Adicionar Horário Especial',
  'add_patient': 'Adicionar Paciente',
  'change_patient': 'Editar Paciente',
  'delete_patient': 'Excluir Paciente',
  'view_patient': 'Visualizar Paciente',
  'add_doctor': 'Adicionar Médico',
  'change_doctor': 'Editar Médico',
  'delete_doctor': 'Excluir Médico',
  'view_doctor': 'Visualizar Médico',
  // Adicione outros conforme necessário
};
function translateAppLabel(appLabel) {
  return appLabelTranslations[appLabel] || appLabel || 'Outros';
}
function translatePermissionName(permission) {
  // Tenta traduzir pelo codename
  if (permissionTranslations[permission.codename]) {
    return permissionTranslations[permission.codename];
  }
  // Se não encontrar, retorna o nome original
  return permission.name;
}
</script>

<style scoped>
.clinic-btn {
  background: var(--clinic-btn-bg) !important;
  color: #fff !important;
  transition: background 0.2s;
}
.clinic-btn:hover {
  background: var(--clinic-btn-bg-hover) !important;
}
.clinic-btn-secondary {
  background: var(--clinic-btn-bg-hover) !important;
  color: #fff !important;
  transition: background 0.2s;
}
.clinic-btn-secondary:hover {
  background: var(--clinic-btn-bg) !important;
}
.clinic-text {
  color: var(--clinic-font-color) !important;
}
/* Rodapé mais discreto */
p.text-center.text-sm.mb-11.mt-10.clinic-text {
  opacity: 0.5;
  font-size: 12px;
  margin-top: 2rem;
  margin-bottom: 0.5rem;
}

/* Animações para mobile */
.animate-fade-in {
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

/* Melhorias para touch em mobile */
@media (max-width: 768px) {
  .cursor-pointer {
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }
  
  .cursor-pointer:active {
    transform: scale(0.98);
  }
}

/* Adicionando ajustes para modal mobile */
@media (max-width: 768px) {
  .modal-mobile-full {
    max-height: 100vh !important;
    height: 100vh !important;
    border-radius: 0 !important;
    padding-bottom: 0 !important;
  }
  .modal-mobile-content {
    max-height: calc(100vh - 120px) !important;
    padding-bottom: 80px !important;
  }
}
</style> 